import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import useCurrentLocale from '@modules/localization/hooks/useCurrentLocale';
import { v4 as uuidv4 } from 'uuid';
import {
  TDialogClosedInteraction,
  TDialogOpenedInteraction,
  trackSearchDialogClosed,
  trackSearchDialogOpened,
} from '@modules/search/searchEvents';
import { ESearchInteraction } from '@modules/eventStream/enum/DocsSiteSearch';
import NavigationSearchContext, {
  CloseSearchDialogWithInteractionParams,
  OpenSearchDialogWithInteractionParams,
} from './NavigationSearchContext';

type NavigationSearchState = {
  searchSessionId: string;
  searchDialogOpen: boolean;
};

const NavigationSearchProvider: FunctionComponent<React.PropsWithChildren> = ({ children }) => {
  // to avoid race conditions, always set searchSessionId with isOpen
  const [{ searchDialogOpen, searchSessionId }, setSearchState] = useState<NavigationSearchState>({
    searchSessionId: uuidv4(),
    searchDialogOpen: false,
  });
  const { urlLocale: locale } = useCurrentLocale();

  // open -> open:      keep searchSessionId
  // open -> closed:    keep searchSessionId
  // closed -> closed:  keep searchSessionId
  // closed -> open:    new searchSessionId
  const setSearchDialogOpen = useCallback(
    (
      update: React.SetStateAction<boolean>,
      interaction: TDialogOpenedInteraction | TDialogClosedInteraction,
    ) => {
      setSearchState((prevState) => {
        const { searchDialogOpen: prevSearchDialogOpen, searchSessionId: prevSearchSessionId } =
          prevState;
        // React.SetStateAction: the parameter can be either an "update function" or the actual value
        const nextSearchDialogOpen =
          typeof update === 'function' ? update(prevSearchDialogOpen) : update;

        // only set a new searchSessionId if we go from closed to open
        // only track if we go from closed to open
        if (prevSearchDialogOpen === false && nextSearchDialogOpen === true) {
          const nextSearchSessionId = uuidv4();
          // couldn't get discriminated unions working with the 'update' param
          // typechecks for interaction being the correct type should happen in caller code
          // @ts-expect-error `interaction` type is verified in caller code
          trackSearchDialogOpened({ interaction, locale, searchSessionId: nextSearchSessionId });
          return {
            searchDialogOpen: true,
            searchSessionId: nextSearchSessionId,
          };
        }

        // only track if we go from open to closed
        if (prevSearchDialogOpen === true && nextSearchDialogOpen === false) {
          // @ts-expect-error `interaction` type is verified in caller code
          trackSearchDialogClosed({ interaction, locale, searchSessionId: prevSearchSessionId });
          return {
            searchDialogOpen: false,
            searchSessionId: prevSearchSessionId,
          };
        }
        return prevState;
      });
    },
    [locale],
  );

  const setSearchDialogOpenWithEvent = useCallback(
    ({
      searchDialogOpen: nextSearchDialogOpen,
      interaction,
    }: OpenSearchDialogWithInteractionParams | CloseSearchDialogWithInteractionParams) => {
      setSearchDialogOpen(nextSearchDialogOpen, interaction);
    },
    [setSearchDialogOpen],
  );

  const toggleSearchDialogOpenWithEvent = useCallback(
    ({ interaction }: { interaction: ESearchInteraction.Shortcut }) => {
      setSearchDialogOpen((prevSearchDialogOpen: boolean) => !prevSearchDialogOpen, interaction);
    },
    [setSearchDialogOpen],
  );

  const value = useMemo(
    () => ({
      searchDialogOpen,
      searchSessionId,
      setSearchDialogOpenWithEvent,
      toggleSearchDialogOpenWithEvent,
    }),
    [
      searchDialogOpen,
      searchSessionId,
      setSearchDialogOpenWithEvent,
      toggleSearchDialogOpenWithEvent,
    ],
  );

  return (
    <NavigationSearchContext.Provider value={value}>{children}</NavigationSearchContext.Provider>
  );
};

export default NavigationSearchProvider;
